/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

/**
 * ============================================================================
 * Notification Store - Zustand State Management
 * ============================================================================
 *
 * Centralized notification/toast state management.
 * Supports auto-dismiss timers with configurable durations per notification type.
 *
 * ============================================================================
 */

import { create } from 'zustand';

/**
 * Notification types
 */
export type NotificationType = 'success' | 'error' | 'warning' | 'info';

/**
 * Notification entity
 */
export interface Notification {
  id: string;
  type: NotificationType;
  title: string;
  message: string;
  duration?: number;                      // ms, undefined = manual close only
  fieldErrors?: Record<string, string>;   // For inline field error display
  createdAt: number;                      // Timestamp for ordering
}

/**
 * Default durations per notification type (in milliseconds)
 */
export const DEFAULT_DURATIONS: Record<NotificationType, number | undefined> = {
  success: 5000,     // 5 seconds
  info: 5000,        // 5 seconds
  warning: 8000,     // 8 seconds
  error: undefined,  // Manual close only
};

/**
 * Notification state interface
 */
interface NotificationState {
  // State
  notifications: Notification[];

  // Actions
  addNotification: (notification: Omit<Notification, 'id' | 'createdAt'>) => string;
  removeNotification: (id: string) => void;
  clearAll: () => void;
  clearByType: (type: NotificationType) => void;
}

/**
 * Generate unique notification ID
 */
const generateId = (): string => {
  return `notif_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
};

/**
 * Maximum number of notifications to show at once
 */
const MAX_NOTIFICATIONS = 5;

/**
 * Notification store
 */
export const useNotificationStore = create<NotificationState>((set, get) => ({
  notifications: [],

  /**
   * Add a new notification
   * Returns the notification ID for manual dismissal
   */
  addNotification: (notification) => {
    const id = generateId();
    const newNotification: Notification = {
      ...notification,
      id,
      createdAt: Date.now(),
      // Apply default duration if not specified
      duration: notification.duration !== undefined
        ? notification.duration
        : DEFAULT_DURATIONS[notification.type],
    };

    set((state) => {
      // Remove oldest notifications if exceeding max
      let notifications = [...state.notifications, newNotification];
      if (notifications.length > MAX_NOTIFICATIONS) {
        notifications = notifications.slice(-MAX_NOTIFICATIONS);
      }
      return { notifications };
    });

    // Set up auto-dismiss if duration is specified
    if (newNotification.duration) {
      setTimeout(() => {
        get().removeNotification(id);
      }, newNotification.duration);
    }

    return id;
  },

  /**
   * Remove a notification by ID
   */
  removeNotification: (id) => {
    set((state) => ({
      notifications: state.notifications.filter((n) => n.id !== id),
    }));
  },

  /**
   * Clear all notifications
   */
  clearAll: () => {
    set({ notifications: [] });
  },

  /**
   * Clear notifications by type
   */
  clearByType: (type) => {
    set((state) => ({
      notifications: state.notifications.filter((n) => n.type !== type),
    }));
  },
}));

/**
 * Selector hooks for common use cases
 */
export const useNotifications = () => useNotificationStore((state) => state.notifications);
export const useNotificationCount = () => useNotificationStore((state) => state.notifications.length);
export const useErrorNotifications = () =>
  useNotificationStore((state) => state.notifications.filter((n) => n.type === 'error'));
